<template>
  <div :class="specialPageCarouselClass">
    <!-- 単一バナー -->
    <template v-if="carouselData.length === 1">
      <div class="specialPageCarousel__soloImg">
        <a v-for="item in carouselData" :key="item.id" :href="`/specialPage/detail/?spid=${item.alias}`">
          <img :src="item.url" :alt="item.alt" />
        </a>
      </div>
    </template>

    <!-- 複数バナー -->
    <template v-else-if="carouselData.length > 1">
      <div class="specialPageCarousel__slider">
        <div class="specialPageCarousel__sliderBody">
          <AppCarousel2 :mergeOptions="carouselMergeOptions">
            <SwiperSlide v-for="item in carouselData" :key="item.id">
              <a :href="`/specialPage/detail/?spid=${item.alias}`">
                <img class="object-fit h-max-40" :src="item.url" :alt="item.alt" />
              </a>
            </SwiperSlide>
          </AppCarousel2>
        </div>
      </div>

      <div v-if="showMore" class="specialPageCarousel__more">
        <TextButton to="/specialPage" position="right">
          もっと見る<Icon name="arrow_right" :color="colorCode" />
        </TextButton>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { SwiperSlide } from "swiper/vue";
import AppCarousel2 from "@/general/components/carousels/Carousel2.vue";
import { useStore } from "vuex";

interface Category { category_id: string; category_code?: string; }
interface ItemCategory { data_category_id: string; data_category_code?: string; }
interface CarouselItem {
  title: string;
  alias: string;
  alt: string;
  id: string;
  url: string;
  filename: string;
  publish_location: string[];
  special_category: ItemCategory[];
}

export default defineComponent({
  name: "SpecialPageCarousel",
  components: { AppCarousel2, SwiperSlide },
  props: {
    cmsResponse: { type: Object, default: null },
    viewArea: { type: String, default: "" },
    specialCategory: { type: String, default: null },
    showMore: { type: Boolean, default: true }
  },
  setup(props) {
    const store = useStore();
    const colorCode = computed(() => store.state.commons.colorCode);
    const carouselData = ref<CarouselItem[]>([]);
    const specialPageCarouselClass = computed(() =>
      props.showMore ? "specialPageCarousel__Showmore" : "specialPageCarousel"
    );

    const isMobile = window.innerWidth < 1024;

    // slidesPerView をバナー枚数に応じて自動調整
    const carouselMergeOptions = computed(() => {
      const slidesCount = carouselData.value.length;
      const defaultSlides = isMobile ? 1.3 : 3;
      const slidesPerView = slidesCount < defaultSlides ? slidesCount : defaultSlides;

      return {
        slidesPerView,
        spaceBetween: 24,
        loop: true,
        centeredSlides: isMobile,
        breakpoints: {
          1024: { slidesPerView: Math.min(slidesCount, 3), centeredSlides: false }
        }
      };
    });

    // CMSレスポンスから carouselData を作成
    const makeCarouselData = (obj: any) => {
      if (!obj || !obj.included) return;
      const included = obj.included;

      const specialCategories: Category[] = included
        .filter((el: any) => el.type === "taxonomy_term--special_category")
        .map((cat: any) => ({ category_id: cat.id, category_code: cat.attributes.field_tag_code }));

      const dataList: CarouselItem[] = obj.data.map((item: any) => {
        const imageSrc = included.find((el: any) => el.id === item.relationships.field_banner.data.id);
        const categoryIds: ItemCategory[] = item.relationships.special_category.data.map((cat: any) => {
          const findName = specialCategories.find(c => c.category_id === cat.id);
          return { data_category_id: cat.id, data_category_code: findName?.category_code };
        });

        return {
          title: item.attributes.title,
          alias: item.attributes.alias,
          alt: item.relationships.field_banner.data.meta.alt,
          id: item.relationships.field_banner.data.id,
          url: process.env.VUE_APP_CMS_ASSET_DOMAIN + imageSrc.attributes.uri.url,
          filename: imageSrc.attributes.filename,
          publish_location: item.attributes.publish_location,
          special_category: categoryIds
        };
      });

      filterBySpecialCategory(dataList);
    };

    const filterBySpecialCategory = (data: CarouselItem[]) => {
      const filtered = data.filter(item =>
        !props.specialCategory || item.special_category.some(cat => cat.data_category_code === props.specialCategory)
      );
      filterByViewArea(filtered);
    };

    const filterByViewArea = (data: CarouselItem[]) => {
      const filtered: CarouselItem[] = [];
      data.forEach(item => {
        item.publish_location.forEach(loc => {
          if ((props.viewArea === "top" && loc === "Home") || (props.viewArea === "list" && loc === "Listing")) {
            filtered.push(item);
          }
        });
      });
      carouselData.value = filtered;
    };

    onMounted(() => makeCarouselData(props.cmsResponse));

    return {
      carouselData,
      colorCode,
      specialPageCarouselClass,
      carouselMergeOptions
    };
  }
});
</script>

<style scoped lang="scss">
.specialPageCarousel { padding-top: 1rem; }
.specialPageCarousel__Showmore { padding-top: 1rem; margin-bottom: 3rem; }
.specialPageCarousel__soloImg { text-align: center; }
.specialPageCarousel__sliderBody { position: relative; width: calc(100% + 2rem); margin-left: -1rem; }
.specialPageCarousel__more { text-align: right; font-size: 0.875rem; }
.object-fit { object-fit: cover; }
.h-max-40 { max-height: 160px; }
</style>
